import type { HeroSlide } from 'ui-kit'
import type { CmsImage } from '@src/types/cms'
import type { HomepageCarouselAttributes } from '@src/types/strapi'

const DEFAULT_SLIDE_BACKGROUND = '#000000'

const cmsImageUrl = (image?: CmsImage): string =>
  image?.data?.attributes?.url ?? ''

const cmsImageAlt = (image?: CmsImage): string =>
  image?.data?.attributes?.alternativeText ?? ''

export const plainTextFromMarkdown = (value?: string): string => {
  if (!value) return ''

  return value
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .trim()
}

export const splitCarouselMarkdown = (
  value?: string,
): { title: string; bodyText?: string } => {
  const text = value?.trim() ?? ''
  if (!text) return { title: '' }

  const blocks = text.split(/\n{2,}/)
  const title = plainTextFromMarkdown(blocks[0] ?? '')
  const bodyText = blocks
    .slice(1)
    .map(plainTextFromMarkdown)
    .filter(Boolean)
    .join('\n')

  return { title, ...(bodyText ? { bodyText } : {}) }
}

const isLightTextColour = (colour?: string): boolean => {
  if (!colour) return true

  const normalized = colour.trim().toLowerCase()
  if (
    normalized === 'white' ||
    normalized === '#fff' ||
    normalized === '#ffffff'
  ) {
    return true
  }

  const hex = normalized.replace('#', '')
  const fullHex =
    hex.length === 3
      ? hex
          .split('')
          .map(char => `${char}${char}`)
          .join('')
      : hex

  if (!/^[0-9a-f]{6}$/.test(fullHex)) return true

  const red = parseInt(fullHex.slice(0, 2), 16)
  const green = parseInt(fullHex.slice(2, 4), 16)
  const blue = parseInt(fullHex.slice(4, 6), 16)
  const luminance = (0.299 * red + 0.587 * green + 0.114 * blue) / 255

  return luminance > 0.6
}

export const mapHomepageCarouselToHeroSlide = (
  attributes: HomepageCarouselAttributes,
  index: number,
): HeroSlide => {
  const { title, bodyText } = splitCarouselMarkdown(attributes.title)
  const buttons = attributes.buttons ?? attributes.button ?? []
  const cta = buttons.find(button => button?.text && button?.link)
  const desktopImage = attributes.backgroundImage
  const mobileImage = attributes.mobileBackgroundImage
  const logoUrl = cmsImageUrl(attributes.logo)
  const backgroundColor =
    attributes.backgroundColour ??
    attributes.mobileHeaderColour ??
    DEFAULT_SLIDE_BACKGROUND

  return {
    id: title || index,
    backgroundColor,
    textTheme: isLightTextColour(attributes.titleColour) ? 'light' : 'dark',
    title,
    ...(bodyText ? { bodyText } : {}),
    imageDesktopSrc: cmsImageUrl(desktopImage),
    imageMobileSrc: cmsImageUrl(mobileImage) || cmsImageUrl(desktopImage),
    imageAlt: cmsImageAlt(desktopImage) || cmsImageAlt(mobileImage) || title,
    ...(logoUrl && {
      logoAssetSrc: logoUrl,
      logoAssetAlt: cmsImageAlt(attributes.logo),
    }),
    ...(cta && {
      ctaButton: {
        label: cta.text ?? '',
        href: cta.link ?? '',
        isOpenInNewTab: cta.target === '_blank',
      },
    }),
  }
}
